<template>
  <div class="game-service">
    <subcategories-block class="container game-core__subcategories" />

    <div
      class="container game-service__header"
      :class="{ 'game-service__header--only-sort': !isShowTitle }">
      <div class="game-service__title">
        <template v-if="isShowTitle">
          {{ !isSubcategory ? 'Top offers' : (activeTag.seo_heading || activeTag.title) }}
        </template>
      </div>

      <product-sort
        class="game-service__product-sort"
        :active-option="activeSortOption"
        :sort-options="sortOptions"
        @sortOption="changeCurrentSort" />
    </div>

    <base-card-list
      :items="availableProducts"
      show-need-help
      infinity-scroll
      class="container"
      @load-more="loadMore">
      <template #card="{ item }">
        <product-card
          :product="item"
          :show-card-footer="showProductCardsFooter" />
      </template>
    </base-card-list>

    <div
      v-if="unavailableProducts.length"
      class="game-service__unavailable container"
      :class="{'game-service__unavailable--open': isUnavailableOpen}">
      <div
        class="game-service__block-title game-service__block-title--clickable"
        @click="isUnavailableOpen = !isUnavailableOpen">
        Unavailable offers
        <arrowhead-icon :is-reverse="isUnavailableOpen" />
      </div>

      <base-card-list
        v-show="isUnavailableOpen"
        :items="unavailableProducts">
        <template #card="{ item }">
          <product-card
            :product="item" />
        </template>
      </base-card-list>
    </div>

    <hidden-pagination
      v-if="hiddenPaginationParams.lastPage > 1"
      :params="hiddenPaginationParams" />

    <how-it-works class="game-service__how container" />

    <video-about class="game-service__video container" />

    <we-try-hard class="game-service__we-try-hard" />

    <reviews-slider
      v-if="reviews.length"
      :key="activeGame.id"
      title="SkyCoach reviews"
      :reviews="reviews"
      :breakpoints="sliderBreakpoints"
      class="container game-service__reviews" />

    <follow-us class="game-service__follow-us container" />

    <breadcrumbs
      class="container game-service__breadcrumbs"
      :enable-schema="false"
      :items="breadcrumbs" />

    <request-engine
      ref="requestEngine"
      :url="requestEngineUrl"
      :options="requestEngineOptions"
      :stop-requests="stopRequests"
      :should-request-on-mounted="false"
      @response="updateProducts"
      @set-busy="isBusy = $event" />

    <footer-seo-text
      :text="seoText"
      class="game-service__seo-text container" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import AppModel from 'Models/AppModel';
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import HowItWorks from 'Root/pages/game/components/HowItWorks.vue';
import WeTryHard from 'Root/pages/game/components/WeTryHard.vue';
import ReviewsSlider from 'Components/Reviews/ReviewsSlider.vue';
import FollowUs from 'Root/pages/game/components/FollowUs.vue';
import Breadcrumbs from 'Components/ui/Breadcrumbs/Breadcrumbs.vue';
import VideoAbout from 'Components/VideoAbout/VideoAbout.vue';
import RequestEngine from 'Root/pages/game/components/RequestEngine.vue';
import axios from 'axios';
import { scrollTo, setNotFoundState } from 'Root/helpers/tools';
import seoMixin from 'Root/mixins/seoMixin';
import FooterSeoText from 'Components/FooterSeoText.vue';
import SortModel from 'Models/SortModel';
import RegionModel from 'Models/RegionModel';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import criteoAnalytics from 'Root/drivers/criteoAnalytics';
import BaseCardList from 'Components/ui/BaseCardList/BaseCardList.vue';
import ProductCard from 'Root/pages/game/components/ProductCard.vue';
import { flatTagsById } from 'Root/helpers/tags';
import HiddenPagination from 'Root/pages/game/components/HiddenPagination.vue';
import GameModel from 'Models/GameModel';
import { ABTestPriceDecreaseForNewUsers } from 'Root/drivers/ABTestsDriver';
import SubcategoriesBlock from '../components/SubcategoriesBlock.vue';
import ProductSort from '../components/ProductSort.vue';

const REQUEST_LIMIT = 20;

export default {
  name: 'GameService',

  components: {
    HiddenPagination,
    SubcategoriesBlock,
    FooterSeoText,
    RequestEngine,
    VideoAbout,
    Breadcrumbs,
    FollowUs,
    ReviewsSlider,
    WeTryHard,
    HowItWorks,
    ArrowheadIcon,
    ProductSort,
    BaseCardList,
    ProductCard,
  },

  mixins: [seoMixin],

  seo() {
    let title = AppModel.APP_TITLE;
    let description = null;
    let canonicalUrl = null;

    if (this.activeTag) {
      title = this.activeTag.seo_title || `${AppModel.APP_TITLE} - ${this.activeTag.title}`;
      description = this.activeTag.seo_meta_description || null;
      canonicalUrl = this.activeTag.canonical_url || null;
    }

    const urn = typeof window === 'undefined'
      ? this.$ssrContext.url
      : window.location.pathname;

    const path = urn.split('?')[0];
    const page = +this.$route.query.page || 1;

    const relNext = page < Math.ceil(this.productOffers.total / REQUEST_LIMIT)
      ? `${AppModel.URL}${path}?page=${page + 1}`
      : null;

    let relPrev = null;
    if (page === 2) {
      relPrev = `${AppModel.URL}${path}`;
    } else if (page > 2) {
      relPrev = `${AppModel.URL}${path}?page=${page - 1}`;
    }

    const {
      lowPrice, highPrice, ratingValue, ratingCount, total,
    } = this.activeTag?.micromarkup ?? {};

    const product = {
      name: title,
      description,
      offer: {
        type: 'AggregateOffer',
        url: this.$route.fullPath,
        priceCurrency: this.activeRegion.currency.title,
        lowPrice: lowPrice?.[this.activeRegion.shortTitle.toLowerCase()],
        highPrice: highPrice?.[this.activeRegion.shortTitle.toLowerCase()],
        offerCount: total,
        inStock: !!total,
      },
      rating: {
        value: ratingValue,
        count: ratingCount,
      },
    };

    return {
      title,
      meta: {
        description,
        'og:type': 'website',
        'og:title': title,
        'og:url': `${AppModel.URL}${urn}`,
        'og:image': `${GameModel.getGameBackground(this.$store.state.activeGame.id)}-1400.jpg`,
        'og:description': description,
      },
      canonical: canonicalUrl,
      relNext,
      relPrev,
      product,
    };
  },

  beforeRouteLeave(to, from, next) {
    if (to.name !== 'game__product-detail') {
      this.mutateProductOffers({
        offers: [],
        total: 0,
      });
    }
    next();
  },

  async asyncData({ store, context }) {
    let offset = 0;

    const { page } = context.req.query;
    if (page && !Number.isNaN(page) && page >= 1) {
      offset = (page - 1) * REQUEST_LIMIT;
    }

    const params = {
      offset,
      limit: REQUEST_LIMIT,
      region: store.state.activeRegion.id,
      with_game: store.state.activeGame.id,
      tag_from_url: context.canonical,
    };

    await axios.get(`${AppModel.API_URI}/product/list`, { params })
      .then(({ data }) => {
        if (data.result.length === 0) {
          setNotFoundState(store, context);
        }

        store.commit('products/mutateProductOffers', {
          offers: data.result,
          total: data.total,
        });
      });

    if (context.getCatalogJSONData() && Object.keys(context.getCatalogJSONData()).length !== 0) {
      store.dispatch('setCatalogJSONData', context.getCatalogJSONData());
    }
  },

  data() {
    return {
      isUnavailableOpen: false,
      requestEngineUrl: `${AppModel.API_URI}/product/list`,
      push: false,
      offset: 0,
      sortType: SortModel.TYPE_POPULARITY,
      desc: true,
      stopRequests: false,
      shouldLoadMore: false,
      isBusy: false,
      activeSortOption: SortModel.OPTION_POPULAR_FIRST,
      sortOptions: [
        {
          id: SortModel.OPTION_POPULAR_LAST,
          title: 'Least Popular',
        },
        {
          id: SortModel.OPTION_POPULAR_FIRST,
          title: 'Most Popular',
        },
        {
          id: SortModel.OPTION_PRICE_UP,
          title: 'Price: Low to High',
        },
        {
          id: SortModel.OPTION_PRICE_DOWN,
          title: 'Price: High to Low',
        },
      ],
      sliderBreakpoints: {
        1380: {
          perView: 2,
        },
        1760: {
          perView: 3,
        },
        2560: {
          perView: 4,
        },
      },
      isPriceDecreaseUserAdded: false,
    };
  },

  computed: {
    ...mapState(['activeRegion', 'activeGame', 'user', 'reviews']),
    ...mapState('products', ['tags', 'activeTag', 'productOffers', 'product']),
    ...mapGetters('products', ['breadcrumbs']),
    ...mapGetters(['isUsersFirstOrder']),

    seoText() {
      // eslint-disable-next-line camelcase
      return this.activeTag?.seo_meta_content;
    },

    isWow() {
      return this.activeGame.id === GameModel.WOW.id;
    },

    isRootTag() {
      return !this.activeTag?.parent_id;
    },

    isSubcategory() {
      return !!flatTagsById[this.activeTag?.parent_id]?.parent_id;
    },

    isShowTitle() {
      return this.isSubcategory || (!this.isRootTag && !!flatTagsById[this.activeTag?.id]?._children);
    },

    availableProducts() {
      return (this.productOffers?.data ?? []).filter((p) => Array.isArray(p.available_in_regions)
              && p.available_in_regions.includes(this.activeRegion.id));
    },

    availableProductsGameId() {
      return this.productOffers.data.length ? this.productOffers.data[0].game_id : null;
    },

    unavailableProducts() {
      return (this.productOffers?.data ?? []).filter((p) => !Array.isArray(p.available_in_regions)
              || !p.available_in_regions.includes(this.activeRegion.id));
    },

    showProductCardsFooter() {
      if (!this.activeTag) {
        return false;
      }

      // надежный костыль, на века
      const [firstTagTitle] = this.activeTag._categories;
      return firstTagTitle === 'raids';
    },

    requestEngineOptions() {
      return {
        offset: {
          value: this.offset,
        },
        limit: {
          value: REQUEST_LIMIT,
        },
        tag: {
          value: this.activeTag ? this.activeTag.id : false,
        },
        region: {
          value: this.activeRegion.id,
        },
        with_game: {
          value: this.activeGame.id,
        },
        sort: {
          value: this.sortType,
        },
        desc: {
          value: this.desc,
        },
      };
    },

    sortMap() {
      return {
        [SortModel.OPTION_POPULAR_LAST]: {
          type: SortModel.TYPE_POPULARITY,
          desc: false,
          analyticsEmitText: 'Popular first',
        },
        [SortModel.OPTION_POPULAR_FIRST]: {
          type: SortModel.TYPE_POPULARITY,
          desc: true,
          analyticsEmitText: 'Popular first',
        },
        [SortModel.OPTION_PRICE_UP]: {
          type: +this.activeRegion.id === RegionModel.EU.id
            ? +SortModel.TYPE_PRICE_EU
            : SortModel.TYPE_PRICE_US,
          desc: false,
          analyticsEmitText: 'Price up',
        },
        [SortModel.OPTION_PRICE_DOWN]: {
          type: +this.activeRegion.id === RegionModel.EU.id
            ? +SortModel.TYPE_PRICE_EU
            : SortModel.TYPE_PRICE_US,
          desc: true,
          analyticsEmitText: 'Price down',
        },
      };
    },

    hiddenPaginationParams() {
      return {
        link: this.$route.path,
        currentPage: +this.$route.query.page || 1,
        lastPage: Math.ceil(this.productOffers.total / REQUEST_LIMIT),
      };
    },
  },

  watch: {
    '$route.params.categories': {
      handler(newValue, oldValue) {
        if (!oldValue || (newValue && newValue !== oldValue)) {
          this.actualizeTag();
        }
      },
    },

    '$route.params.namespace': {
      handler(newValue, oldValue) {
        if (!oldValue || (newValue && newValue !== oldValue)) {
          this.actualizeTag();
        }
      },
    },

    activeTag(newValue) {
      this.push = false;
      this.stopRequests = false;
      this.offset = 0;
      scrollTo(0, 0);
      if (newValue) {
        this.setHeaderInfo(newValue);
      }
    },

    activeRegion() {
      this.push = false;
      this.stopRequests = false;
      this.offset = 0;
      scrollTo(0, 0);
    },

    availableProducts: {
      handler(newProducts, oldProducts) {
        if (typeof window === 'undefined' || newProducts.toString() === oldProducts?.toString()) {
          return;
        }

        const mappedItems = newProducts.map((item) => item.id);

        criteoAnalytics.fireListingEvent(this.user?.email, this.activeTag, mappedItems);
      },

      immediate: true,
    },

    isPriceDecreaseUserAdded(val) {
      if (val) {
        const {
          testId,
          groupId,
        } = ABTestPriceDecreaseForNewUsers.userGroup;

        googleTagAnalytics.fireGtagEvent(`ab_test_${testId}_${groupId}`);
      }
    },
  },

  afterAsyncData({ store, context }) {
    const { activeTag, tags } = store.state.products;
    const foundTag = tags.find((t) => t._href === context.canonical);

    if (!foundTag) {
      setNotFoundState(store, context);
    }

    if (activeTag) {
      store.commit('products/mutateEntity', {
        entity: 'headerInfo',
        data: {
          title: activeTag.seo_heading || activeTag.title,
          tagDescription: activeTag?.description,
        },
      });
    }
  },

  async created() {
    this.actualizeTag();

    if (!this.$isSSR) {
      this.$store.dispatch('updateReviewsImagesByProducts');

      if (this.$store.state.catalogJSONData === null) {
        this.$axios
          .get(`/storage/${process.env.FILESYSTEM_RESOURCE_CATALOG_DATA_PATH}?v=${Date.now()}`)
          .then(({ data }) => {
            this.$store.dispatch('setCatalogJSONData', data);
          });
      }

      if (this.$route.query.page && !Number.isNaN(this.$route.query.page) && this.$route.query.page >= 1) {
        this.offset = (this.$route.query.page - 1) * REQUEST_LIMIT;
      }
    }

    if (
      this.isWow
            && this.$route.query?.utm_medium === 'cpc'
            && (!this.user || this.isUsersFirstOrder)
    ) {
      if (await ABTestPriceDecreaseForNewUsers.addUserToTest()) {
        this.isPriceDecreaseUserAdded = true;
      }
    }
  },

  beforeMount() {
    this.setHeaderInfo(this.activeTag);
    googleTagAnalytics.emitProductListNew(this.activeGame.fullTitle, this.productOffers.data, this.activeRegion);
  },

  methods: {
    ...mapMutations('products', ['mutateProductOffers']),

    actualizeTag(routeParams = null) {
      const params = routeParams || this.$route.params;
      // ставим тег если пришли например из футера со SPA страницы
      if (!this.$isSSR) {
        const { categories } = params;

        // SKY-5931: do not refresh tag if it's already active
        if (categories === this.activeTag?._categories.join('/') && this.productOffers.data.length
                && this.availableProductsGameId) {
          return;
        }

        if (categories && typeof categories === 'string') {
          axios.get(`${AppModel.API_URI}/tag`, {
            params: {
              seo_url: categories.split('/').at(-1),
              game_id: this.$store.state.activeGame.id,
            },
          })
            .then(({ data }) => {
              this.$store.commit('products/setActiveTag', data);
              this.offset = 0;
              this.$nextTick(() => {
                this.$refs?.requestEngine?.request();
              });
            }).catch((e) => {
              console.log(e.message);
            });
        }
      }
    },

    setHeaderInfo(tag) {
      if (!tag) {
        return;
      }

      this.$store.commit('products/mutateEntity', {
        entity: 'headerInfo',
        data: {
          title: tag.seo_heading || tag.title,
          tagDescription: tag?.description,
        },
      });
    },

    loadMore() {
      if (this.isBusy) {
        this.shouldLoadMore = true;
      } else {
        this.push = true;
        this.offset += REQUEST_LIMIT;
      }
    },

    updateProducts(data) {
      if (this.push) {
        this.mutateProductOffers({
          offers: [...this.productOffers.data, ...data.result],
          total: data.total,
        });
        this.push = false;
      } else {
        this.mutateProductOffers({
          offers: data.result,
          total: data.total,
        });
      }

      // if response was empty
      if (!data?.result?.length || data?.result?.length < REQUEST_LIMIT) {
        this.stopRequests = true;
        this.shouldLoadMore = false;
      }

      if (this.shouldLoadMore) {
        this.shouldLoadMore = false;
        this.push = true;
        this.offset += REQUEST_LIMIT;
      }

      googleTagAnalytics.emitProductListNew(this.activeGame.fullTitle, data.result, this.activeRegion);
    },

    changeCurrentSort(id) {
      if (this.sortMap[id]) {
        const { type, desc, analyticsEmitText } = this.sortMap[id];
        this.sortType = type;
        this.desc = desc;
        this.push = false;
        this.offset = 0;
        this.stopRequests = false;
        googleTagAnalytics.emitChangeCatalogOrder(analyticsEmitText);

        this.activeSortOption = id;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @media screen and #{$screen-less-than-1024} {
    .game-service__product-sort {
      position: absolute !important;
      right: var(--padding);
      top: 20px;
    }
    .game-service__reviews {
      margin-bottom: 24px;
      padding: 0;

      /deep/.reviews__header {
        margin-left: var(--padding);
        margin-right: var(--padding);
      }

      /deep/.reviews__body {
        padding: 0 var(--padding);
        scroll-padding-left: var(--padding);
      }
    }
  }

  .game-core__subcategories {
    margin-right: 0;
  }

  .game-service__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.game-service__header--only-sort {
      margin: -16px auto;
    }
  }

  .game-service__title {
    font-weight: 500;
    font-size: $text-25px;
    line-height: 30px;
    color: $color--basic-white;
  }

  .game-service__unavailable {
    margin-top: 20px;
  }

  .game-service__block-title {
    display: grid;
    grid-template-columns: 1fr 24px;
    align-items: center;
    font-size: 17px;
    font-weight: 500;
    margin-bottom: -50px;
  }

  .game-service__block-title--clickable {
    cursor: pointer;
  }

  .game-service__unavailable--open {
    .game-service__block-title {
      margin-bottom: 20px;
    }
  }

  .game-service__how {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .game-service__video {
    padding-top: 25px;
    padding-bottom: 0;
  }
  .game-service__we-try-hard {
    padding: 25px 0;
  }

  .game-service__breadcrumbs {
    margin-bottom: 0;
  }

  @media screen and #{$screen-more-or-1024} {
    .game-core__subcategories {
      margin-right: auto;
    }

    .game-service__block-title {
      font-size: 25px;
    }

    .game-service__unavailable {
      margin-top: 50px;
    }

    .game-service__video,
    .game-service__breadcrumbs {
      display: none;
    }

    .game-service__we-try-hard {
      padding: 65px 0 25px;
    }

    .game-service__reviews {
      padding-top: 0;
    }

    .game-service__how {
      margin-top: 70px;
    }

    .game-service__follow-us {
      margin-top: 45px;
      margin-bottom: 45px;
    }
  }
</style>
